import {
  GameDetails,
  GameMode,
  GameStatus,
  Player,
  PlayerDetails,
  Ranking,
  UserAccount,
} from "../types/Game";
import lodash, { some } from "lodash";
import Firebase from "./Firebase";
import { FINAL_BOARD_POSITION, SINGLE_DIE_POSITION } from "../config/Constants";

const getFirstPlayer = (players: PlayerDetails[]): PlayerDetails => {
  const [firstItem] = players;
  return firstItem;
};

const isPlayerThrowingDice = (user: PlayerDetails, game: GameDetails) => {
  const throwingDice =
    (game.status === GameStatus.RollDice ||
      game.status === GameStatus.ReadingCard) &&
    game.turnUserId === user.userId;
  console.log("throwingDice:", throwingDice);
  return throwingDice;
};

const isWaitingForPlayers = (user: UserAccount, game: GameDetails) => {
  if (game.mode === GameMode.PlayAlone) {
    return false;
  }
  return game.ownerUserId === user.userId &&
    game.status === GameStatus.WaitingPlayers
    ? true
    : false;
};

const getPlayerTrowingDice = (
  players: Player[],
  game: GameDetails
): Player | undefined => {
  return players.find((item) => item.profile.userId === game.turnUserId);
};

const getPlayerBoardPosition = (
  players: Player[],
  game: GameDetails
): number | undefined => {
  const player = players.find(
    (item) => item.profile.userId === game.turnUserId
  );
  return player ? player.profile.boardPosition : undefined;
};

const getLocalPlayer = (
  players: PlayerDetails[],
  user: UserAccount
): PlayerDetails | undefined => {
  const player = players.find((item) => item.userId === user.userId);
  return player;
};

const getPlayer = (
  players: PlayerDetails[],
  userId: string
): PlayerDetails | undefined => {
  const player = players.find((item) => item.userId === userId);
  return player;
};

const getNextPlayerToThrowDice = (
  players: Player[],
  game: GameDetails
): Player | null => {
  const activePlayers = players.filter(
    (item) => item.profile.boardPosition !== FINAL_BOARD_POSITION
  );
  const userThrowingDice = getPlayerTrowingDice(activePlayers, game);
  if (!userThrowingDice) {
    return null;
  }
  const index = lodash.findIndex(
    activePlayers,
    (item) => item.spriteId === userThrowingDice.spriteId
  );
  return getNextPlayer(index, activePlayers);
};

const getNextPlayer = (
  currentPosition: number,
  players: Player[]
): Player | null =>
  currentPosition >= 0 ? players[currentPosition + 1] || players[0] : null;

const getTotal = (numbers: number[]) => {
  return Math.round(
    numbers
      ? numbers.reduce(
          (accumulator, currentValue) => accumulator + currentValue,
          0
        )
      : 0
  );
};

const getPlayersActivePlaying = (players: PlayerDetails[]) => {
  return players.filter((player) => !hasPlayerFinished(player));
};

const getEndingGamePlayerPoints = (players: PlayerDetails[]): number => {
  const finishPosition = players.filter((player) => hasPlayerFinished(player));
  if (finishPosition.length === 0) {
    return 4;
  }

  if (finishPosition.length === 1) {
    return 3;
  }

  if (finishPosition.length === 2) {
    return 2;
  }

  if (finishPosition.length === 3) {
    return 1;
  }

  return 0;
};

const sendFinalPointsToServer = (players: PlayerDetails[]) => {};

const hasPlayerFinished = (player: PlayerDetails) => {
  return player.boardPosition >= FINAL_BOARD_POSITION;
};

const hasPlayerReachedFinalPosition = (player?: Player) => {
  if (!player) {
    return false;
  }
  return player.profile.boardPosition >= FINAL_BOARD_POSITION;
};

const getPlayersGameRanking = (players: PlayerDetails[]): Ranking[] => {
  const rawPlayers = lodash.orderBy(players, ["finishTimeStamp"], ["asc"]);
  const ranking = rawPlayers.map((item, index) => ({
    name: item.name,
    avatar: item.profileUrl,
    position: index + 1,
    points: getTotal(item.points || [0]),
    country: item.countryCode,
    countryCode: item.countryCode,
  }));

  const ordered = lodash.orderBy(
    ranking,
    ["position", "points"],
    ["asc", "asc"]
  );

  return ordered;
};

const addPlayerPoints = async (player: Player, score: number) => {
  const points: number[] = player.profile.points ? player.profile.points : [];
  points.push(score);
  await Firebase.updateGamePlayer(
    player.profile.gameId,
    player.profile.userId,
    {
      points,
    }
  );
};

const addPlayerMagnfyingGlass = async (
  player: PlayerDetails,
  quantity: number
) => {
  const magnifyGlass: number[] = player.magnifyGlass ? player.magnifyGlass : [];
  magnifyGlass.push(quantity);
  await Firebase.updateGamePlayer(player.gameId, player.userId, {
    magnifyGlass,
  });
};
const calCulateMagnfyingGlass = (
  players: PlayerDetails[],
  user: UserAccount
): number => {
  const player = getLocalPlayer(players, user);
  return player ? getTotal(player.magnifyGlass || []) : 0;
};

const calCulatePlayerPoints = (
  players: PlayerDetails[],
  user: UserAccount
): number => {
  const player = getLocalPlayer(players, user);
  return player ? getTotal(player.points || []) : 0;
};

const isDicePaired = (magnifyGlass: number[]): boolean => {
  return magnifyGlass &&
    magnifyGlass[0] &&
    magnifyGlass[1] &&
    magnifyGlass[1] === magnifyGlass[0]
    ? true
    : false;
};

const getNumberOfDices = (position: number): number => {
  return position >= SINGLE_DIE_POSITION ? 1 : 2;
};

export default {
  addPlayerMagnfyingGlass,
  addPlayerPoints,
  getFirstPlayer,
  isPlayerThrowingDice,
  getPlayerTrowingDice,
  getTotal,
  getNumberOfDices,
  getNextPlayerToThrowDice,
  isWaitingForPlayers,
  getPlayerBoardPosition,
  hasPlayerFinished,
  hasPlayerReachedFinalPosition,
  getPlayersActivePlaying,
  getPlayersGameRanking,
  getEndingGamePlayerPoints,
  getLocalPlayer,
  calCulatePlayerPoints,
  calCulateMagnfyingGlass,
  isDicePaired,
  getPlayer,
};
